import React, { useState } from 'react';
import { AlertCircle, Send, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';

interface ClarificationBannerProps {
  message: string;
  requestedBy?: string;
  requestedAt?: string;
  onRespond: (responseText: string) => Promise<void>;
  disabled?: boolean;
}

export const ClarificationBanner: React.FC<ClarificationBannerProps> = ({
  message,
  requestedBy = 'Approver',
  requestedAt,
  onRespond,
  disabled = false,
}) => {
  const [responseText, setResponseText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!responseText.trim()) {
      setError('Please enter your clarification response.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await onRespond(responseText);
      setResponseText('');
    } catch {
      setError('Failed to submit response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="border-amber-500/50 bg-amber-500/10 dark:bg-amber-500/5 shadow-sm">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-500/20 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-foreground">Clarification Requested by {requestedBy}</h3>
              {requestedAt && (
                <span className="text-[11px] text-foreground-secondary font-mono">
                  {new Date(requestedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            <p className="text-xs text-foreground bg-surface/60 p-3 rounded-lg border border-amber-500/20 italic">
              "{message}"
            </p>
          </div>
        </div>

        {!disabled && (
          <form onSubmit={handleSubmit} className="space-y-3 pt-1 border-t border-amber-500/20">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5 text-primary" /> Your Response & Additional Information
              </label>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Provide detailed explanation or clarification for the approver..."
                rows={3}
                className="w-full text-xs bg-surface border border-border rounded-lg p-2.5 text-foreground placeholder:text-foreground-secondary/50 focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {error && <p className="text-xs font-semibold text-destructive">{error}</p>}

            <div className="flex justify-end">
              <Button type="submit" size="sm" isLoading={isSubmitting}>
                <Send className="h-3.5 w-3.5 mr-1.5" /> Submit Clarification Response
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};
